import { createRef, forwardRef, ReactNode, Ref, useEffect } from "react";

import { sv } from "date-fns/locale";
import DatePicker, {
  CalendarContainer,
  ReactDatePickerProps,
  registerLocale,
  setDefaultLocale,
} from "react-datepicker";

import CalendarIcon from "@/assets/icons/calendar.svg";
import { useI18n } from "@/contexts/i18nContext";
import { formatDateForApi } from "@/utils";
import Button from "@components/Button";

import styles from "./AESimpleDatepicker.module.css";

registerLocale("sv", sv);

const ref = createRef<HTMLButtonElement>();

// Props for the custom button
interface DatePickerButtonProps {
  onClick?: () => void;
  label: string | undefined;
  isInvalid?: boolean;
}

// ForwardRef to pass down the ref
const DatePickerButton = forwardRef<HTMLButtonElement, DatePickerButtonProps>(
  ({ onClick, label }, ref) => (
    <Button
      className={styles.button}
      onClick={onClick}
      ref={ref as Ref<HTMLButtonElement>}
      icon={CalendarIcon}
    >
      {label}
    </Button>
  )
);

DatePickerButton.displayName = "DatePickerButton";

type AESimpleDatepickerProps = ReactDatePickerProps & {
  isInvalid?: boolean;
  placeholderText?: string;
};

interface ContainerProps {
  className?: string;
  children?: ReactNode;
}

const AESimpleDatepicker = (props: AESimpleDatepickerProps) => {
  const { language } = useI18n();

  useEffect(() => {
    setDefaultLocale(language);
  }, [language]);

  const Container = ({ className, children }: ContainerProps) => (
    <CalendarContainer className={className}>
      <div className="react-datepicker__triangle"></div>
      <div style={{ position: "relative" }}>{children}</div>
    </CalendarContainer>
  );

  return (
    <DatePicker
      {...props}
      customInput={
        <DatePickerButton
          ref={ref}
          label={
            props.selected
              ? formatDateForApi(new Date(props.selected as Date))
              : props.placeholderText
          }
        />
      }
      calendarContainer={Container}
    />
  );
};

export { AESimpleDatepicker };
